import {
  Box,
  Card,
  Code,
  Flex,
  IconButton,
  List,
  ListItem,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
  UnorderedList,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiCloseLine, RiErrorWarningLine } from "@remixicon/react";
import type { ReactNode } from "react";

import { Restricted } from "@/app/components/Restricted";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import { DebugEventTypeDisplay } from "@/common/components/DebugEventTypeSelect";
import { satisfactionScoreToText } from "@/common/components/EventCell";
import { WarningBadgeTooltip } from "@/common/components/InfoIconTooltip";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import { RouterLinkButton } from "@/common/components/RouterLinkButton";
import { SectionHeading } from "@/common/components/SectionHeading";
import {
  SidebarContainer,
  SidebarSection,
  SidebarTable,
} from "@/common/components/SidebarLayout";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import type { DebugEvent } from "@reflag/shared/eventAPI";
import { NewFeatureUrl } from "@reflag/shared/urls";

function CodeWithEllipsis({ children }: { children: ReactNode }) {
  return (
    <Tooltip label={children} openDelay={1000}>
      <Box noOfLines={1} wordBreak="break-all">
        <Code as="span" display="inline" textOverflow="ellipsis">
          {children}
        </Code>
      </Box>
    </Tooltip>
  );
}

function JsonFieldSidebarSection({
  label,
  content,
}: {
  label: string;
  content: unknown;
}) {
  const codeBg = useColorModeValue("gray.50", "gray.750");
  return (
    <Flex direction="column" gap={2}>
      <Text color="dimmed" fontSize="sm" fontWeight="medium">
        {label}
      </Text>
      <Code
        bg={codeBg}
        borderRadius="base"
        overflowX="auto"
        px={3}
        py={1.5}
        whiteSpace="pre"
      >
        {content ? JSON.stringify(content, null, 2) : "No data"}
      </Code>
    </Flex>
  );
}

function SharedFieldsSidebarSection({
  event,
  showEventType,
}: {
  event: DebugEvent;
  showEventType: boolean;
}) {
  return (
    <SidebarTable layout="fixed">
      <Tbody>
        {showEventType && (
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              Type
            </Td>
            <Td>
              <Flex justify="flex-end">
                <DebugEventTypeDisplay type={event.debugEventType} />
              </Flex>
            </Td>
          </Tr>
        )}
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            User
          </Td>
          <Td>
            <Flex justify="flex-end">
              {event.user ? (
                <UserDisplay fontSize="sm" link user={event.user} />
              ) : (
                <NotAvailableCell />
              )}
            </Flex>
          </Td>
        </Tr>
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            User ID
          </Td>
          <Td textAlign="right">
            {event.user?.id ? (
              <CodeWithEllipsis>{event.user.id}</CodeWithEllipsis>
            ) : (
              <NotAvailableCell />
            )}
          </Td>
        </Tr>
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            Company
          </Td>
          <Td>
            <Flex justify="flex-end">
              {event.company ? (
                <CompanyDisplay company={event.company} link />
              ) : (
                <NotAvailableCell />
              )}
            </Flex>
          </Td>
        </Tr>
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            <Flex gap={1}>
              <Text noOfLines={1}>Company ID</Text>
              {event.company?.inferred && (
                <WarningBadgeTooltip text="This company ID was inferred from previous association of the user with this company ID" />
              )}
            </Flex>
          </Td>
          <Td textAlign="right">
            {event.company?.id ? (
              <CodeWithEllipsis>{event.company.id}</CodeWithEllipsis>
            ) : event.debugEventType === "user" ? (
              <NotAvailableCell />
            ) : (
              <WarningBadgeTooltip
                label="Missing"
                text="Company ID is missing from this event. Client is possibly set up incorrectly."
              />
            )}
          </Td>
        </Tr>
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            Timestamp
          </Td>
          <Td textAlign="right">
            <TimestampCell value={event.timestamp} autoUpdate />
          </Td>
        </Tr>
        {event.serviceName?.trim() && (
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              Service
            </Td>
            <Td textAlign="right">
              <CodeWithEllipsis>{event.serviceName}</CodeWithEllipsis>
            </Td>
          </Tr>
        )}
        <Tr>
          <Td color="dimmed" fontWeight="medium">
            SDK Version
          </Td>
          <Td textAlign="right">
            {event.sdkVersion ? (
              <CodeWithEllipsis>{event.sdkVersion}</CodeWithEllipsis>
            ) : (
              <NotAvailableCell />
            )}
          </Td>
        </Tr>
        {event.debugEventType === "track" && (
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              Name
            </Td>
            <Td textAlign="right">
              <CodeWithEllipsis>{event.name || ""}</CodeWithEllipsis>
            </Td>
          </Tr>
        )}
        {event.debugEventType === "feedback" && (
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              Score
            </Td>
            <Td textAlign="right">
              <Text fontSize="sm" isTruncated>
                {satisfactionScoreToText(event.feedback?.score || 0)}
              </Text>
            </Td>
          </Tr>
        )}
      </Tbody>
    </SidebarTable>
  );
}

function FeatureFlagEventSidebarSection({
  event,
  showRuleResults,
  showFlagDisplay,
}: {
  event: DebugEvent;
  showRuleResults: boolean;
  showFlagDisplay: boolean;
}) {
  const warningColor = useColorModeValue("orange.800", "orange.200");
  const warningBgColor = useColorModeValue(
    "orange.100",
    "rgba(251, 211, 141, 0.16)",
  );
  const isConfig = event.debugEventType === "check-config";
  const diagnostics = event.featureFlagEvent?.evalErrors ?? [];

  return (
    <>
      <SidebarTable layout="fixed">
        <colgroup>
          <col width="90" />
          <col />
        </colgroup>
        <Tbody>
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              {showFlagDisplay ? "Flag" : "Key"}
            </Td>
            <Td textAlign="right">
              {showFlagDisplay && event.features.length > 0 ? (
                <Flex direction="column" align="flex-end" gap={2}>
                  {event.features.map((feature) => (
                    <FeatureDisplay
                      key={feature.id}
                      feature={feature}
                      includeParents={false}
                      size="sm"
                      link
                    />
                  ))}
                </Flex>
              ) : (
                <CodeWithEllipsis>
                  {event.featureFlagEvent?.featureKey}
                </CodeWithEllipsis>
              )}
            </Td>
          </Tr>
          <Tr>
            <Td color="dimmed" fontWeight="medium">
              {isConfig ? "Config version" : "Flag version"}
            </Td>
            <Td textAlign="right">
              {event.featureFlagEvent?.targetingVersion ? (
                <CodeWithEllipsis>
                  {event.featureFlagEvent.targetingVersion}
                </CodeWithEllipsis>
              ) : (
                <NotAvailableCell />
              )}
            </Td>
          </Tr>
          {isConfig ? (
            <>
              <Tr>
                <Td color="dimmed" fontWeight="medium">
                  Config key
                </Td>
                <Td textAlign="right">
                  {event.featureFlagEvent?.evalConfig?.key ? (
                    <CodeWithEllipsis>
                      {event.featureFlagEvent.evalConfig.key}
                    </CodeWithEllipsis>
                  ) : (
                    <NotAvailableCell />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td colSpan={2}>
                  <JsonFieldSidebarSection
                    content={event.featureFlagEvent?.evalConfig?.payload}
                    label="Config payload"
                  />
                </Td>
              </Tr>
            </>
          ) : (
            <Tr>
              <Td color="dimmed" fontWeight="medium">
                Value
              </Td>
              <Td textAlign="right">
                <Code>
                  {event.featureFlagEvent?.evalResult ? "enabled" : "disabled"}
                </Code>
              </Td>
            </Tr>
          )}
        </Tbody>
      </SidebarTable>
      <JsonFieldSidebarSection
        content={event.featureFlagEvent?.evalContext}
        label="Evaluation context"
      />
      {showRuleResults && (
        <JsonFieldSidebarSection
          content={event.featureFlagEvent?.evalRuleResults}
          label="Rule results"
        />
      )}
      {diagnostics.length > 0 && (
        <Card bg={warningBgColor} p={3} variant="subtle">
          <Flex color={warningColor} direction="column" gap={2}>
            <Flex alignItems="center" gap={1}>
              <RiErrorWarningLine size={16} />
              <Text fontSize="sm" fontWeight="medium">
                Evaluation diagnostics
              </Text>
            </Flex>
            <UnorderedList px={5}>
              {diagnostics.map((error, index) => (
                <ListItem
                  key={`${error.code}-${error.field}-${error.operator ?? ""}-${index}`}
                >
                  {error.message}
                </ListItem>
              ))}
            </UnorderedList>
          </Flex>
        </Card>
      )}
      {!event.featureFlagEvent?.targetingVersion && (
        <Card bg={warningBgColor} p={3} variant="subtle">
          <Flex color={warningColor} direction="column" gap={2}>
            <Flex alignItems="center" gap={1}>
              <RiErrorWarningLine size={16} />
              <Text fontSize="sm" fontWeight="medium">
                SDK initialization issue
              </Text>
            </Flex>
            <Text fontSize="sm">
              This error could be caused by a few different issues:
            </Text>
            <UnorderedList>
              <ListItem>
                The flag key does not exist and was perhaps mistyped
              </ListItem>
              <ListItem>
                Flag was checked before the SDK finished initializing
              </ListItem>
              <ListItem>
                An error occurred during initialization. Check your console/logs
                for errors and ensure flag checks happen after initialization
                completes.
              </ListItem>
            </UnorderedList>
          </Flex>
        </Card>
      )}
    </>
  );
}

function newFeatureKeyFromEvent(event: DebugEvent): string {
  if (event.debugEventType === "track") return event.name || "";
  if (
    event.debugEventType === "check" ||
    event.debugEventType === "check-config"
  ) {
    return event.featureFlagEvent?.featureKey || "";
  }
  return "";
}

export function DebugEventDetails({
  event,
  showEventType = true,
  showRuleResults = true,
  showFlagDisplay = false,
}: {
  event: DebugEvent;
  showEventType?: boolean;
  showRuleResults?: boolean;
  showFlagDisplay?: boolean;
}) {
  const { env } = useEnvironment();
  const codeBg = useColorModeValue("gray.50", "gray.750");
  const featureKey = newFeatureKeyFromEvent(event);
  const featureName = featureKey
    .replace(/[-_.]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return (
    <>
      <SidebarSection>
        <SharedFieldsSidebarSection
          event={event}
          showEventType={showEventType}
        />
        {(event.debugEventType === "check" ||
          event.debugEventType === "check-config") && (
          <FeatureFlagEventSidebarSection
            event={event}
            showRuleResults={showRuleResults}
            showFlagDisplay={showFlagDisplay}
          />
        )}
        {(event.debugEventType === "user" ||
          event.debugEventType === "company") && (
          <JsonFieldSidebarSection
            content={event.attributes}
            label="Attributes"
          />
        )}
        {event.debugEventType === "feedback" && (
          <Flex direction="column" gap={2}>
            <Text color="dimmed" fontSize="sm" fontWeight="medium">
              Comment
            </Text>
            <Code bg={codeBg} borderRadius="base" px={3} py={1.5}>
              {event.feedback?.comment || ""}
            </Code>
          </Flex>
        )}
      </SidebarSection>
      {event.debugEventType !== "company" &&
        event.debugEventType !== "user" && (
          <SidebarSection>
            <SectionHeading>Related flags</SectionHeading>
            <Box minH={6}>
              {event.features.length === 0 ? (
                <Text color="dimmed">No related flags</Text>
              ) : (
                <Flex as={List} direction="column" gap={4}>
                  {event.features.map((feature) => (
                    <ListItem key={feature.id}>
                      <FeatureDisplay
                        feature={feature}
                        includeParents={false}
                        link
                      />
                    </ListItem>
                  ))}
                </Flex>
              )}
            </Box>
            {event.features.length === 0 && (
              <Restricted role="contributor">
                <RouterLinkButton
                  size="sm"
                  to={NewFeatureUrl(env, {
                    key: featureKey,
                    name: featureName,
                  })}
                  variant="outline"
                  width="100%"
                >
                  Track new flag
                </RouterLinkButton>
              </Restricted>
            )}
          </SidebarSection>
        )}
    </>
  );
}

export function SidebarEventDetails({
  event,
  onClose,
}: {
  event: DebugEvent;
  onClose: () => void;
}) {
  return (
    <SidebarContainer position="sticky" top={0}>
      <Flex align="center">
        <SectionHeading flexGrow={1}>Event details</SectionHeading>
        <IconButton
          aria-label="Close event details"
          icon={<RiCloseLine size={16} />}
          size="xs"
          variant="ghost"
          onClick={onClose}
        />
      </Flex>
      <DebugEventDetails event={event} />
    </SidebarContainer>
  );
}
